import { apiRequest, type components } from './core';
import { getSubdomain } from '@/utils/environment';
import type { TenantConfig } from '@shared/types/tenant-config';

/**
 * Login response from backend
 * Note: refreshToken is now in httpOnly cookie (not in response body)
 */
export type LoginResponse = components['schemas']['LoginResponse'];

/**
 * Refresh token response from backend
 */
export type RefreshResponse = components['schemas']['RefreshResponse'];

/**
 * Auth API endpoints
 */
export const authApi = {
  /**
   * Login with email and password
   * Returns JWT access token and refresh token
   */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const subdomain = getSubdomain();

    // Build headers manually (no auth token needed for login)
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.error || 'Login failed');
    }

    return response.json();
  },

  /**
   * Refresh access token using refresh token from httpOnly cookie
   * No body needed - cookie is sent automatically with credentials: 'include'
   */
  refresh: async (): Promise<RefreshResponse> => {
    const subdomain = getSubdomain();

    // Build headers manually (no auth token needed for refresh)
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers,
      credentials: 'include', // Important: sends httpOnly cookie
      // No body - refresh token is in httpOnly cookie
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.error || 'Token refresh failed');
    }

    return response.json();
  },

  /**
   * Logout current user
   * Revokes refresh token server-side and clears cookies
   * Note: Refresh token is read from httpOnly cookie by backend
   */
  logout: async () => {
    return apiRequest<{ success: boolean; message: string }>('/api/auth/logout', {
      method: 'POST',
    });
  },

  /**
   * Get current user info
   * Used after login to fetch user details and validate tenant access
   */
  getMe: async () => {
    return apiRequest<{
      success: boolean;
      user: {
        id: string;
        userId: string;
        email: string;
        role: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string | null;
        tenantId: string;
        emailVerified?: boolean;
      };
      tenant: {
        id: string;
        subdomain: string;
        schoolName: string;
        timezone: string; // School timezone from backend
        settings: TenantConfig;
        allowedModules: {
          attendance: boolean;
          parentPortal: boolean;
          sms: boolean;
          messaging: boolean;
          schoolwideSms: boolean;
        };
      };
    }>('/api/auth/me');
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email: string, subdomain: string) => {
    return apiRequest<{ success: boolean; message: string }>('/api/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email, subdomain }),
    });
  },

  /**
   * Validate password reset token
   */
  validateResetToken: async (token: string) => {
    return apiRequest<{
      success: boolean;
      valid: boolean;
      email?: string;
    }>('/api/auth/validate-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token: string, newPassword: string) => {
    return apiRequest<{ success: boolean; message: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  },

  /**
   * Validate staff invitation token
   */
  validateInvitationToken: async (token: string) => {
    return apiRequest<{
      success: boolean;
      valid: boolean;
      user?: {
        email: string;
        firstName: string;
        lastName: string;
      };
    }>('/api/auth/validate-invitation-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Set password via invitation token
   */
  setPassword: async (token: string, password: string) => {
    return apiRequest<{ success: boolean; message: string }>('/api/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  /**
   * Verify email with token (public - no auth)
   */
  verifyEmail: async (token: string) => {
    const subdomain = getSubdomain();
    const response = await fetch('/api/auth/verify-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(subdomain ? { 'X-Tenant-Subdomain': subdomain } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{ success: boolean; message: string; emailChanged?: boolean }>;
  },

  /**
   * Resend verification email (authenticated, parent-only)
   */
  resendVerification: async () => {
    return apiRequest<{ success: boolean; message: string }>('/api/auth/resend-verification', {
      method: 'POST',
    });
  },
};

/**
 * Profile API endpoints (self-service user profile management)
 */
export const profileApi = {
  /**
   * Update current user's profile
   * Allowed fields: firstName, lastName, phoneNumber
   */
  updateProfile: async (updates: { firstName?: string; lastName?: string; phoneNumber?: string }) => {
    return apiRequest<{
      success: boolean;
      data: {
        id: string;
        userId: string;
        email: string;
        firstName: string;
        lastName: string;
        phoneNumber: string | null;
        role: string;
        tenantId: string;
      };
    }>('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Change password with current password verification
   */
  changePassword: async (currentPassword: string, newPassword: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>('/api/profile/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  /**
   * Request email change (parent-only, sends verification to new address)
   */
  changeEmail: async (newEmail: string, confirmEmail: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>('/api/profile/change-email', {
      method: 'POST',
      body: JSON.stringify({ newEmail, confirmEmail }),
    });
  },
};

/**
 * Session data returned from backend
 */
export interface Session {
  id: string;
  deviceName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string;
  isCurrent: boolean;
}

/**
 * Session API endpoints (manage active sessions)
 */
export const sessionApi = {
  /**
   * Get all active sessions for current user
   */
  getSessions: async () => {
    return apiRequest<{
      success: boolean;
      sessions: Session[];
    }>('/api/auth/sessions');
  },

  /**
   * Revoke a specific session
   */
  revokeSession: async (sessionId: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Log out from all devices (revoke all sessions)
   */
  logoutAllDevices: async () => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>('/api/auth/logout-all', {
      method: 'POST',
    });
  },
};
